import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FractionInput, { formatLatexPreview } from '../../../../components/inputs/FractionInput';

// ── helpers ──────────────────────────────────────────
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const powi = (a, k) => (k >= 0 ? Math.pow(a, k) : 1 / Math.pow(a, -k));
const isIntegerOrProperFraction = (value) => Number.isInteger(value) || (Math.abs(value) > 0 && Math.abs(value) < 1);

// „log” z ukrytą podstawą 10 (do poleceń, inputów, odpowiedzi)
const logb = (a) => (a === 10 ? '\\log' : `\\log_{${a}}`);
// jawne wypisanie podstawy w wyjaśnieniach (od kroku 2)
const logExp = (a) => (a === 10 ? '\\log_{10}' : `\\log_{${a}}`);

// NWD + uproszczenia ułamków
const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
};

function simplifyFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

// aproksymacja liczby rzeczywistej ułamkiem (ułamki łańcuchowe)
function toFraction(x, maxDen = 10000) {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  if (!isFinite(x)) return { num: sign, den: 0 };
  let h0 = 0, k0 = 1, h1 = 1, k1 = 0;
  let y = x;
  for (let i = 0; i < 20; i++) {
    const a = Math.floor(y);
    const h2 = a * h1 + h0;
    const k2 = a * k1 + k0;
    if (k2 > maxDen) break;
    h0 = h1; k0 = k1; h1 = h2; k1 = k2;
    const frac = y - a;
    if (frac < 1e-12) break;
    y = 1 / frac;
  }
  let num = h1, den = k1;
  if (k0 !== 0 && Math.abs(h0 / k0 - x) < Math.abs(num / den - x)) {
    num = h0; den = k0;
  }
  ({ num, den } = simplifyFrac(num * sign, den));
  return { num, den };
}

// zapis ładnej liczby jako LaTeX (całkowita / zwykła / mieszana)
function niceLatexFromNumber(val) {
  if (!isFinite(val)) return String(val);
  const int = Math.round(val);
  if (Math.abs(val - int) < 1e-12) return String(int);

  let { num, den } = toFraction(val);
  ({ num, den } = simplifyFrac(num, den));
  const sgn = num < 0 ? '-' : '';
  num = Math.abs(num);

  return `${sgn}\\tfrac{${num}}{${den}}`;
}

// czy c jest „czystą potęgą” a (dla małych wykładników)
function isPowerOfBase(c, a) {
  for (let k = -6; k <= 6; k++) {
    if (Math.abs(c - Math.pow(a, k)) < 1e-10) return true;
  }
  return false;
}

// „ładne” mnożniki – jak w zadaniach z sumą i różnicą
const NICE_BY_BASE = {
  10: [2, 4, 5, 8, 25, 50, 1/2, 1/4, 4/5, 20, 1/5],
   5: [2, 4, 8, 10, 1/2, 1/4],
   4: [2, 8, 1/2, 10, 1/4, 2/5, 5],
   3: [2, 6, 1/2, 3/4, 12, 1/4],
   2: [3, 6, 3/4, 5, 10, 1/5, 2/5],
};

// pomocnicza: sprawdzamy czy argument logarytmu jest „nie-gołą potęgą” i sensowny
function isNiceLogArg(x, a) {
  if (!isFinite(x) || x <= 0) return false;
  const nearInt = Math.abs(x - Math.round(x)) < 1e-12;
  if (nearInt && isPowerOfBase(Math.round(x), a)) return false;
  return true;
}

// rozkładamy a^t na X·Y·Z z ładnymi mnożnikami, jak w zadaniach z sumą/różnicą
function splitIntoThreeNiceFactors(a, t) {
  const P = powi(a, t);
  const exps = [-2, -1, 0, 1, 2];
  const poolBase = NICE_BY_BASE[a] || NICE_BY_BASE[10];
  const pool = poolBase.filter((c) => c !== 1 && !isPowerOfBase(c, a));

  for (let tries = 0; tries < 80; tries++) {
    const u1 = pick(exps);
    const u2 = pick(exps);
    const c1 = pick(pool);
    const c2 = pick(pool);

    const X = c1 * powi(a, u1);
    const Y = c2 * powi(a, u2);
    const Z = P / (X * Y);

    if (!isNiceLogArg(X, a) || !isNiceLogArg(Y, a) || !isNiceLogArg(Z, a)) continue;
    if (![X, Y, Z].every(isIntegerOrProperFraction)) continue;

    // dbamy też, żeby wartości były różne
    if (Math.abs(X - Y) < 1e-12 || Math.abs(X - Z) < 1e-12 || Math.abs(Y - Z) < 1e-12) continue;

    // kontrola ułamka Z – żeby nie miał gigantycznego mianownika
    const { num, den } = toFraction(Z, 200);
    if (Math.abs(num) > 500 || den > 200) continue;

    return {
      X, Y, Z,
      Xs: niceLatexFromNumber(X),
      Ys: niceLatexFromNumber(Y),
      Zs: niceLatexFromNumber(Z),
    };
  }

  // awaryjny, ale nadal nie-trywialny fallback
  const c = pool[0] || 2;
  const u1 = 1, u2 = -1;
  const X = c * powi(a, u1);
  const Y = c * powi(a, u2);
  const Z = P / (X * Y);
  return {
    X, Y, Z,
    Xs: niceLatexFromNumber(X),
    Ys: niceLatexFromNumber(Y),
    Zs: niceLatexFromNumber(Z),
  };
}

// ── generator ────────────────────────────────────────
// Zadanie: log_a X + log_a Y + log_a Z, gdzie X·Y·Z = a^t
// i żaden z trzech argumentów nie jest „gołą” potęgą a.
export function generateTask() {
  const a = pick([2, 3, 4, 5, 10]); // podstawa a>0, a≠1
  const t = pick([-3, -2, -1, 1, 2, 3, 4]); // wynik

  const { X, Y, Z, Xs, Ys, Zs } = splitIntoThreeNiceFactors(a, t);

  return {
    type: 'LogPropSum3Task',
    data: { a, t, X, Y, Z, Xs, Ys, Zs },
  };
}

// ── renderTask (BlockMath w poleceniu) ────────────────────────────────
export function renderTask({ data }) {
  const { a, Xs, Ys, Zs } = data;
  const expr = `${logb(a)} ${Xs} + ${logb(a)} ${Ys} + ${logb(a)} ${Zs}`;
  return (
    <div>
      Oblicz wartość wyrażenia:
      <div
        style={{
          marginTop: 14,
          marginBottom: 14,
          fontSize: '16px',
          lineHeight: 1.5,
        }}
      >
        <BlockMath math={expr} />
      </div>
    </div>
  );
}

// ── renderInput (bez labela – Podgląd = sama odpowiedź) ──────────────
export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => {
    onChange && onChange(ans);
  };

  return (
    <div className="space-y-2">
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        label=""    // nic nie podgrywa w podglądzie
      />
    </div>
  );
}

// ── renderUserAnswer (tylko odpowiedź, bez lewej strony i „=”) ───────
export function renderUserAnswer(userAnswer, task) {
  if (!userAnswer || typeof userAnswer !== 'object' || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

// ── checkAnswer ───────────────────────────────────────────────────────
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'object') return false;
  const given = userAnswer.parsed;
  if (given == null) return false;
  return Math.abs(given - data.t) < 1e-4;
}

// ── renderAnswer (tylko wynik, bez lewej strony i „=”) ───────────────
export function renderAnswer({ data }) {
  const { t } = data;
  return <InlineMath math={String(t)} />;
}

// ── renderExplanation ────────────────────────────────────────────────
export function renderExplanation({ data }) {
  const { a, Xs, Ys, Zs, t } = data;

  const prod = niceLatexFromNumber(powi(a, t));

  return (
    <div className="space-y-2">
      <p><strong>Krok 1:</strong> Własność sumy logarytmów:</p>
      <BlockMath math={'\\log_{a} x + \\log_{a} y = \\log_{a}(xy)'} />
      <BlockMath math={'\\text{Zatem }\\ (\\log_{a} x + \\log_{a} y) + \\log_{a} z = \\log_{a}(xy) + \\log_{a} z = \\log_{a}(xyz).'} />
      {a === 10 && (
        <p style={{ marginTop: 6 }}>
          <em>Uwaga:</em> zapis <InlineMath math={"\\log"} /> oznacza logarytm dziesiętny, czyli{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            <InlineMath math={"\\log x = \\log_{10} x"} />
          </span>.
        </p>
      )}

      <p><strong>Krok 2:</strong> Podstawiamy i mnożymy:</p>
      <BlockMath
        math={`${logExp(a)} ${Xs} + ${logExp(a)} ${Ys} + ${logExp(a)} ${Zs} = ${logExp(a)}\\!\\left(${Xs}\\cdot${Ys}\\cdot${Zs}\\right)`}
      />
      <p><strong>Krok 3:</strong> Obliczamy:</p>
      <BlockMath math={`${logExp(a)} ${prod} = ${t}`} />

      <p><strong>Wniosek:</strong></p>
      <BlockMath
        math={`${logExp(a)} ${Xs} + ${logExp(a)} ${Ys} + ${logExp(a)} ${Zs} = ${t}`}
      />
    </div>
  );
}
